import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import { PrismicText } from '@landsbankinn/prismic';
import {
  Column,
  Divider,
  Row,
  device,
  typography,
  Reveal,
  useRevealTrail,
} from '@landsbankinn/ui';
import React from 'react';

import { animated } from 'react-spring';
import { RichText } from './RichText';

enum BulletsColor {
  BLUE = 'Blue',
  CYAN = 'Cyan',
  ORANGE = 'Orange',
}

enum TitleSize {
  MEDIUM = 'Medium',
  BIG = 'Big',
  SMALL = 'Small',
}

interface IProps {
  title: Prismic.RichText | undefined;
  titleString?: string;
  titleSize?: 'Medium' | 'Big' | 'Small';
  copy: React.ReactNode;
  copySize?: boolean;
  action?: React.ReactNode;
  pointsType?: 'Empty' | 'Bullets' | 'Numbers' | 'Text' | 'Icon';
  pointsColor?: 'Default' | 'Auto' | 'Blue' | 'LightBlue' | 'Cyan' | 'Orange';
  pointsLocation?: boolean;
  children: React.ReactNode;
  noTop?: boolean;
}

interface ILitstItem {
  content: string | React.ReactNode;
}
const Host = styled(Reveal)``;

const Title = styled.div<{ titleSize: string }>`
  margin-bottom: 30px;
  h1,
  h2 {
    ${(props) => {
      switch (props.titleSize) {
        case TitleSize.MEDIUM:
          return typography.h2;
        case TitleSize.BIG:
          return typography.h2Big;
        case TitleSize.SMALL:
          return typography.h3;
      }
      return '';
    }};
  }

  @media ${device.tablet} {
    margin-bottom: 40px;
  }
`;

const CustomDivider = styled(Divider)`
  margin: 16px 0 30px;

  @media ${device.tablet} {
    margin: 16px 0 40px;
  }
`;

const Copy = styled.div<{ isLarge: boolean }>`
  margin-bottom: 4em;

  @media ${device.tablet} {
    margin-bottom: 0;
  }

  div {
    ${(props) => (!props.isLarge ? typography.p2 : typography.body)}
  }
`;

const Action = styled.div`
  border-top: ${(props) => `1px solid ${props.theme.colors.primary}`};
  padding-top: 20px;
  margin-bottom: 30px;

  @media ${device.tablet} {
    margin-bottom: 0;
    margin-top: 30px;
  }
`;

const Items = styled.div<{
  pointsColor: string;
  isLarge: boolean;
  isBullets?: boolean;
}>`
  display: flex;
  flex-flow: row wrap;
  margin-top: 20px;

  @media ${device.tablet} {
    margin-top: 0;
    ${(props) => !props.isBullets && 'margin-bottom: -4em'};
    padding-top: ${(props) => (props.isLarge ? '5px' : 0)};
  }

  li {
    &:before {
      background: ${(props) => {
        switch (props.pointsColor) {
          case BulletsColor.BLUE:
            return props.theme.shades.primary3;
          case BulletsColor.CYAN:
            return props.theme.shades.ternary5;
          case BulletsColor.ORANGE:
            return props.theme.colors.secondary;
        }
        return '';
      }};
    }
  }
`;

const Ul = styled.ul``;

const Li = styled(animated.li)`
  ${typography.body}
  ${typography.listStyle}
`;

const ItemsRow = styled(Row)<{ noTop?: boolean }>`
  margin-bottom: ${(props) => (props.noTop ? '-2em' : '2em')};

  a {
    margin-bottom: 2em;
  }

  @media ${device.tablet} {
    margin-top: ${(props) => (props.noTop ? '10px' : '4em')};
    margin-bottom: ${(props) => (props.noTop ? '-4em' : '2em')};

    a {
      margin-bottom: 0;
    }
  }
`;

export const ListItem = ({ content }: ILitstItem) => {
  return <Li>{content}</Li>;
};

export const TextWithPointsPage = ({
  title,
  titleString,
  titleSize,
  copy,
  copySize,
  action,
  pointsType,
  pointsColor,
  pointsLocation: isBottomAligned,
  children,
  noTop,
}: IProps) => {
  const { ref, itemsWithStyleProp } = useRevealTrail(children);
  return (
    <Host>
      <Row>
        <Column md={{ width: 6 / 12 }}>
          {title && (
            <Title titleSize={titleSize ?? 'Medium'}>
              <PrismicText value={title} />
            </Title>
          )}
          {titleString && !title && (
            <Title titleSize={titleSize ?? 'Medium'}>{titleString}</Title>
          )}
          {!isBottomAligned && copy && title && <CustomDivider />}
          {!isBottomAligned && pointsType !== 'Bullets' && (
            <Copy isLarge={!!copySize}>{copy}</Copy>
          )}
        </Column>
        <Column
          md={{
            width: !isBottomAligned && pointsType === 'Empty' ? 5 / 12 : 6 / 12,
            offset: !isBottomAligned && pointsType === 'Empty' ? 1 / 12 : 0,
          }}
        >
          {isBottomAligned && copy && <Copy isLarge={!!copySize}>{copy}</Copy>}
          {isBottomAligned && action && <Action>{action}</Action>}
          {!isBottomAligned && pointsType !== 'Bullets' && (
            <Row>
              <Items
                ref={ref}
                pointsColor={pointsColor ?? 'Default'}
                isLarge={!copySize}
              >
                {itemsWithStyleProp}
              </Items>
            </Row>
          )}
        </Column>
      </Row>

      <Row>
        <Column md={{ width: 6 / 12 }}>
          {!isBottomAligned && copy && pointsType === 'Bullets' && (
            <Copy isLarge={!!copySize}>{copy}</Copy>
          )}
          {!isBottomAligned && action && <Action>{action}</Action>}
        </Column>
        <Column md={{ width: 5 / 12, offset: 1 / 12 }}>
          {!isBottomAligned && pointsType === 'Bullets' && (
            <Items
              ref={ref}
              isBullets
              pointsColor={pointsColor ?? 'Default'}
              isLarge={!copySize}
            >
              <Ul>{itemsWithStyleProp}</Ul>
            </Items>
          )}
        </Column>
      </Row>
      {isBottomAligned && (
        <ItemsRow noTop={noTop} ref={ref}>
          {itemsWithStyleProp}
        </ItemsRow>
      )}
    </Host>
  );
};
